"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { SITE } from "@/site";
import {
  CONSENT_CHANGE_EVENT,
  CONSENT_ID_KEY,
  CONSENT_STORAGE_KEY,
  OPEN_PREFS_EVENT,
  POLICY_VERSION,
  readChoice,
  serializeChoice,
  type ConsentChoice,
} from "@/lib/consent";

// Shared consent-log worker (repo gokmenkarasu/consent-log); it accepts this site's origin
const LOG_URL = "https://consent-log.kozmonot-com-tr.workers.dev";

type Action = "accept_all" | "custom" | "reject_all";

function getConsentId() {
  let id: string | null = null;
  try {
    id = localStorage.getItem(CONSENT_ID_KEY);
  } catch {}
  if (!id) {
    const c = document.cookie.match(new RegExp(`(?:^|; )${CONSENT_ID_KEY}=([^;]*)`));
    if (c) id = decodeURIComponent(c[1]);
  }
  if (!id) id = `cid_${Date.now().toString(36)}${Math.random().toString(36).substring(2, 10)}`;
  try {
    localStorage.setItem(CONSENT_ID_KEY, id);
  } catch {}
  const d = new Date();
  d.setFullYear(d.getFullYear() + 2);
  document.cookie = `${CONSENT_ID_KEY}=${encodeURIComponent(id)};expires=${d.toUTCString()};path=/;SameSite=Lax`;
  return id;
}

function logConsent(action: Action, choice: ConsentChoice) {
  try {
    fetch(LOG_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        action,
        categories: { necessary: true, analytics: !!choice.a },
        page_url: window.location.href,
        consent_id: getConsentId(),
        policy_version: POLICY_VERSION,
      }),
    }).catch(() => {});
  } catch {}
}

function Toggle({ checked, disabled, onChange, label }: { checked: boolean; disabled?: boolean; onChange?: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={`relative h-6 w-10 shrink-0 rounded-full transition-colors ${checked ? "bg-accent-blue" : "bg-neutral-300"} ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span className={`absolute left-1 top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
    </button>
  );
}

export function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [draft, setDraft] = useState<ConsentChoice>({ a: 0 });

  useEffect(() => {
    if (!readChoice()) setOpen(true);

    const reopen = () => {
      setDraft(readChoice() ?? { a: 0 });
      setPrefsOpen(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_PREFS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, reopen);
  }, []);

  const save = (choice: ConsentChoice, action: Action) => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, serializeChoice(choice, Date.now()));
    } catch {}
    logConsent(action, choice);
    window.dispatchEvent(new CustomEvent<ConsentChoice>(CONSENT_CHANGE_EVENT, { detail: choice }));
    setOpen(false);
    setPrefsOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie Preferences"
      className="fixed bottom-20 left-4 right-4 z-50 rounded-2xl bg-white p-5 text-[14px] text-neutral-600 shadow-2xl ring-1 ring-black/10 sm:right-auto sm:max-w-sm lg:bottom-4"
    >
      <div className="flex items-start justify-between gap-4">
        <span className="text-[16px] font-bold text-neutral-900">Cookie Preferences</span>
        <button
          type="button"
          aria-label="Close and reject optional cookies"
          onClick={() => save({ a: 0 }, "reject_all")}
          className="-mr-1 -mt-1 h-8 w-8 cursor-pointer rounded-full text-xl leading-none text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900"
        >
          ×
        </button>
      </div>
      <p className="mt-2 leading-relaxed">
        We use necessary cookies, and {SITE.clarityId ? "Google Analytics, Ahrefs Web Analytics and Microsoft Clarity" : "Google Analytics and Ahrefs Web Analytics"} only if you allow them.{" "}
        <Link href="/cookie-policy" className="text-neutral-900 underline underline-offset-2">Cookie Policy</Link>
      </p>

      {prefsOpen && (
        <ul className="mt-4 divide-y divide-neutral-200 border-y border-neutral-200">
          <li className="flex items-center justify-between gap-4 py-3">
            <div>
              <div className="font-semibold text-neutral-900">Necessary</div>
              <div className="text-[12px] text-neutral-500">Required for the site to work and to remember your choice</div>
            </div>
            <Toggle checked disabled label="Necessary" />
          </li>
          <li className="flex items-center justify-between gap-4 py-3">
            <div>
              <div className="font-semibold text-neutral-900">Analytics</div>
              <div className="text-[12px] text-neutral-500">{SITE.clarityId ? "Usage statistics with Google Analytics, Ahrefs and Microsoft Clarity" : "Usage statistics with Google Analytics and Ahrefs"}</div>
            </div>
            <Toggle checked={!!draft.a} label="Analytics" onChange={(v) => setDraft({ a: v ? 1 : 0 })} />
          </li>
        </ul>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setPrefsOpen(!prefsOpen)}
          aria-expanded={prefsOpen}
          className="basis-full cursor-pointer whitespace-nowrap text-left font-medium text-neutral-500 hover:text-neutral-900 sm:mr-auto sm:basis-auto"
        >
          Preferences <span className={`inline-block text-[10px] transition-transform ${prefsOpen ? "rotate-180" : ""}`}>▼</span>
        </button>
        <button
          type="button"
          onClick={() => (prefsOpen ? save(draft, "custom") : save({ a: 0 }, "reject_all"))}
          className="flex-1 cursor-pointer whitespace-nowrap rounded-lg border border-neutral-300 px-3 py-2 font-semibold text-neutral-900 transition-colors hover:border-neutral-900 sm:flex-none"
        >
          {prefsOpen ? "Save Preferences" : "Necessary Only"}
        </button>
        <button
          type="button"
          onClick={() => save({ a: 1 }, "accept_all")}
          className="flex-1 cursor-pointer whitespace-nowrap rounded-lg bg-neutral-900 px-3 py-2 font-semibold text-white transition-colors hover:bg-neutral-700 sm:flex-none"
        >
          Accept All
        </button>
      </div>
    </div>
  );
}
